import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IAccountDetailResponse,
  IAccountFilteredListParams,
  IAccountLicenceMutation,
  IAccountPagesResponse,
  IAccountResponse,
  ICreateAccount,
  IUpdateAccount,
} from './types';

export const useGetMagellanAccountsLightList = () => {
  const { responseHandler } = useResponseHandler();

  return useQuery<IAccountResponse[], DefaultError>({
    queryKey: ['magellan-accounts-light-list'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'MagellanAccount',
        params: { page: 0, itemCount: 999 },
      });

      const value = await responseHandler<IAccountPagesResponse>(response, {
        groupName: 'useGetMagellanAccountsLightList',
      });

      return value.items;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetAccountListInfinite = (
  params: IAccountFilteredListParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<IAccountResponse[], DefaultError>({
    queryKey: ['accounts', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'MagellanAccount',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<IAccountPagesResponse>(response, {
        groupName: `useGetAccountListInfinite - ${pageParam}`,
      });

      return infiniteHandler<IAccountResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetAccount = (id: string, refetchOnWindowFocus?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IAccountDetailResponse, DefaultError>({
    queryKey: ['account', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MagellanAccount/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useGetAccount',
        notFoundPath: '/account',
      });
    },
    refetchOnWindowFocus,
  });
};

export const usePostAccount = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostAccount');
  return useMutation<IAccountResponse, DefaultError, ICreateAccount>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('account.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'MagellanAccount',
        body: body,
      });

      return responseHandler(response, {
        groupName: 'usePostAccount',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.accountName ?? i18next.t('account.what'),
        })
      );
    },
    onError: dismiss,
  });
};
export const useUpdateAccount = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useUpdateAccount');
  return useMutation<IAccountResponse, DefaultError, IUpdateAccount>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('account.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'MagellanAccount',
        body: body,
      });

      return responseHandler(response, {
        groupName: 'useUpdateAccount',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.accountName ?? i18next.t('account.what'),
        })
      );
    },
    onError: dismiss,
  });
};
export const usePostLicenseFromMagellan = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostLicenseFromMagellan'
  );
  return useMutation<IAccountResponse, DefaultError, string>({
    mutationFn: async magellanAccountId => {
      loading(
        i18next.t('service-message.update.loading', {
          what: 'licence',
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'MagellanAccount/SetFromMagellan',
        body: { magellanAccountId },
      });

      return responseHandler(response, {
        groupName: 'usePostLicenseFromMagellan',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: 'licence',
        })
      );
    },
    onError: dismiss,
  });
};

export const useSetAccountLicense = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useSetAccountLicense');
  return useMutation<IAccountResponse, DefaultError, IAccountLicenceMutation>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('account.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'MagellanAccount/SetLicense',
        body,
      });

      return responseHandler(response, {
        groupName: 'useSetAccountLicense',
        notFoundPath: '/account',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.accountName ?? i18next.t('account.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteAccount = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteAccount');
  return useMutation<{}, DefaultError>({
    async mutationFn() {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('account.what'),
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `MagellanAccount/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteAccount',
        notFoundPath: '/account',
      });
    },

    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('account.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useAccountRefreshVersion = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useAccountRefreshVersion'
  );
  return useMutation<IAccountResponse, DefaultError, string>({
    mutationFn: async magellanAccountId => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('account.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'MagellanAccount/RefreshVersions',
        body: { magellanAccountId },
      });

      return responseHandler(response, {
        groupName: 'useAccountRefreshVersion',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.accountName ?? i18next.t('account.what'),
        })
      );
    },
    onError: dismiss,
  });
};
